import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, createRef, useRef } from 'react';
import {
	FormContainer,
	DataForm,
	PageContainerActionParamType,
	Loader,
	DataFormFieldChangeParamType,
	SaveDataParamType,
	useStateContext,
} from "@vegi.toenlioe/novitate-react-components";
import GroupBrg from '../../../models/GroupBrg';
import { validate as uuidValidate } from "uuid";
import { MenuItems } from '../../../utils/Menu';

const groupName: string = "Stok";
const subgroupName: string = "Master";
const code: string = "STOCK-GROUP-BRG";
const title: string = "Barang";
const endpoint = "groupbrg";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<GroupBrg>({});
    const divisiRef = useRef();
	const navigate = useNavigate();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);
	
	useEffect(() => {
		if (!location?.state?.ucode)
			return;

        setLoading(true);

		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
				const data: GroupBrg | null = response.data?.data ? {
					...response.data?.data,
					// add additional properties here	
				} : null;
				setFormData(data? {...data} : {});
			})
            .catch((error) => console.error({error}))
            .finally(() => {
				setLoading(false);
			})
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: GroupBrg | undefined) => {
        if (!data?.nama)
            return false;

        if (data?.ucode && !data.kode)
            return false;

        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<GroupBrg> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const dataToSend = {
            ucode: param.data.ucode,
            kode: param.data.kode,
            nama: param.data.nama,
			is_jasa: param.data.is_jasa,
			is_sub_brg: param.data.is_sub_brg,
			is_sub_jasa: param.data.is_sub_jasa,
			aktif: param.data.aktif,
			keterangan: param.data.keterangan,
        };

        const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(dataToSend)) : apiCaller.post(endpoint, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
		<>
        {
            loading && <Loader />
        }
			<FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
			>
				<DataForm<GroupBrg>
					menuItems={MenuItems}
					groupName={groupName}
					subgroupName={subgroupName}
					code={code}
					title={title}
					data={formData}
					isViewMode={isViewMode}
					fields={[
						{
							name: "kode",
							title: "Kode Group Barang",
							inputType: "text",
							dataType: (typeof formData.kode),
							isReadonly: false,
							isRequired: formData?.ucode ? true : false,
							onChanged: (valueObj: DataFormFieldChangeParamType<GroupBrg>) => onChanged(valueObj),
						},
						{
							name: "nama",
							title: "Nama Group Barang",
							inputType: "text",
							dataType: (typeof formData.nama),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<GroupBrg>) => onChanged(valueObj),
						},
						{
							name: "is_jasa",
							title: "Jasa",
							inputType: "checkbox",
							dataType: (typeof formData.is_jasa),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<GroupBrg>) => onChanged(valueObj),
						},
						{
							name: "is_sub_brg",
							title: "Sub Material",
							inputType: "checkbox",
							dataType: (typeof formData.is_sub_brg),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<GroupBrg>) => onChanged(valueObj),
						},
						{
							name: "is_sub_jasa",
							title: "Sub Order",
							inputType: "checkbox",
							dataType: (typeof formData.is_sub_jasa),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<GroupBrg>) => onChanged(valueObj),
						},
						{
							name: "keterangan",
							title: "Keterangan",
							inputType: "textarea",
							dataType: (typeof formData.keterangan),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<GroupBrg>) => onChanged(valueObj),
							rows: 3,
						},
					]}
					onValidate={onValidate}
					onClose={(pathObject) => onClose(pathObject)}
					onSave={(data) => onSave(data)}
				/>
			</FormContainer>
		</>
    );
};

export default Form;
